import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatSliderChange } from '@angular/material/slider';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RepaymentMortgage } from './models/mortgage.model';

const mNear = v => Math.round(v * 100) / 100

class Repayment {
  date
  constructor(startDate, pmtNum) {
    const pipe =  new DatePipe('en-GB')
    const date = new Date(startDate).setMonth(new Date(startDate).getMonth() + pmtNum)
    const formatted = pipe.transform(date, 'dd MMM yyyy')
    this.date = formatted
  }
}

@Injectable({
  providedIn: 'root'
})
export class CalcsService {

  hpi = {
    "Detached": 0.1057,
    "Semi-Detached": 0.0957,
    "Terraced": 0.0971,
    "Flat": 0.0600
  }

  rm: RepaymentMortgage = {
    originalPropertyVal: 260000,
    loanTerm: 30,
    initialTerm: 2,
    depositVal: 10,
    interestRate: 0.0348,
    mortgageFee: 1210,
    addFees: true,
    cbAmt: 750,
    extraPmt: 0,
    pmtsPerYear: 12,
    hpiVal: this.hpi['Detached'],
    propertyType: 'Detached'
  }

  amended = false

  constructor(private snackBar: MatSnackBar) {
   }

   onInputChange(event: MatSliderChange, type: string) {
     this.amended = true
     console.log(this.amended)
    switch (type) {
      case 'op':
        this.rm.extraPmt = event.value
        break;
      case 'hpi':
        this.rm.hpiVal = event.value
        break;
      case 'initial-term':
        this.rm.initialTerm = event.value
        break;
      case 'term':
        this.rm.loanTerm = event.value
        break;
      case 'deposit':
        this.rm.depositVal = event.value
        break;
    }
  }

  getChartData() {
    let chartData = []
    const filtered = this.getSchedule().filter((_,i) => i % 12 == 0)

    for (let i = 0; i < filtered.length; i++) {
      chartData.push({Year: i , Remaining: parseInt(filtered[i].prinBalPostExtra), Principal: filtered[i].prinBalPostExtra * 0.4, Interest: filtered[i].prinBalPostExtra * 0.6})
    }

    return chartData
  }

  parseCB() {
    if (this.rm.cbAmt[0] == "£") {
      return parseInt(this.rm.cbAmt.toString().substring(1))
    } else {
      return this.rm.cbAmt
    }
  }

  parseFee() {
    if (this.rm.mortgageFee[0] === "£") {
      return parseInt(this.rm.mortgageFee.toString().substring(1))
    } else {
      return this.rm.mortgageFee
    }
  }

  initialTermMonths() {
    return (this.rm.initialTerm * 12) - 1 //As schedule array starts from 0
  }

  //***////Chris Methods
  calcPerPeriodRate() {
    return this.rm.interestRate / this.rm.pmtsPerYear
  }

  calcPerPeriodHpi() {
    const annualHpiIncrease = this.rm.hpiVal
    return Math.pow((1 + annualHpiIncrease), (1 / 12)) - 1
  }

  calcNumPmts() {
    return this.rm.loanTerm * this.rm.pmtsPerYear
  }

  calcPmt() {
    const perPeriodRate = this.calcPerPeriodRate()
    return this.getLoanAmount() * perPeriodRate / (1 - (1 + perPeriodRate) ** (-this.calcNumPmts()))
  }

  calcTotalPmts() {
    return this.calcNumPmts() * this.calcPmt()
  }

  getSchedule() {
    let schedule = []

    let prevPropertyValue = this.rm.originalPropertyVal
    let prevPrinBal = this.getLoanAmount()
    let hasFullyPaid = false

    for (let i=0; i<this.calcNumPmts(); i++) {
      schedule.push(new Repayment(new Date(), i))
    }

    schedule.forEach(r => {
      
      if (!hasFullyPaid) {

        r.pmt = -this.calcPmt()
        r.intPmt = prevPrinBal * this.calcPerPeriodRate()
        
        let prinPmt = -r.pmt - r.intPmt

        // check if this pmt fully pays the mortgage
        if (prevPrinBal - prinPmt < 0) {
          hasFullyPaid = true
          r.prinPmt = prevPrinBal
          r.pmt = -r.prinPmt - r.intPmt
          r.prinBalPreExtra = 0
          r.extraPmt = 0
          r.prinBalPostExtra = 0
        } else {
          r.prinPmt = prinPmt
          r.prinBalPreExtra = prevPrinBal - r.prinPmt
          // check if this extraPmt fully pays the mortgage
          if (r.prinBalPreExtra + this.rm.extraPmt < 0) {
            hasFullyPaid = true
            r.extraPmt = -r.prinBalPreExtra
            r.prinBalPostExtra = 0
          } else {
            r.extraPmt = this.rm.extraPmt
            r.prinBalPostExtra = r.prinBalPreExtra - r.extraPmt
          }
        }
      } else {
        r.pmt = 0
        r.intPmt = 0
        r.prinPmt = 0
        r.prinBalPreExtra = 0
        r.extraPmt = 0
        r.prinBalPostExtra = 0
      }

      prevPrinBal = r.prinBalPostExtra

      r.propertyValue = prevPropertyValue * (1 + this.calcPerPeriodHpi())
      r.ltv = r.prinBalPostExtra / r.propertyValue
      prevPropertyValue = r.propertyValue
    })

    return schedule
  }

  calcActualNumPayments() {
    return this.getSchedule().filter(r => mNear(r.prinPmt + r.extraPmt) != 0).length
  }

  calcNumPaymentsSaved() {
    return this.calcNumPmts() - this.calcActualNumPayments()
  }

  // calcTotalExtraPayments() {
  //   return -this.getSchedule().filter(r => mNear(r.prinPmt + r.extraPmt) != 0).map(r => r.extraPmt).reduce((p, c) => p + c)
  // }

  calcOriginalInterest() {
    return -mNear(this.getLoanAmount() - this.calcPmt() * this.calcNumPmts())
  }

  calcActualInterest() {
    const filtered = this.getSchedule().filter(r => mNear(r.prinPmt + r.extraPmt) != 0).map(r => r.intPmt)

    if (filtered.length > 0) {
      const totalActualInterest = mNear(filtered.reduce((p, c) => p + c))
      return totalActualInterest
    } else {
      return 0
    }
  }

  calcInterestSaved() {
    return mNear(this.calcOriginalInterest() - this.calcActualInterest())
  }

  calcLtv() {
    const schedule = this.getSchedule()
    let prevPropertyValue = this.rm.originalPropertyVal
    schedule.forEach(r => {
      r.propertyValue = prevPropertyValue * (1 + this.calcPerPeriodHpi())
      r.ltv = r.prinBalPostExtra / r.propertyValue
      
      prevPropertyValue = r.propertyValue
    })
    return schedule[this.initialTermMonths()].ltv
  }

  getLoanAmount() {
    return this.rm.originalPropertyVal - this.getDepositAmount() + (this.rm.addFees ? this.parseFee() : 0) - this.parseCB()
  }
  
  getLoanAmountWithoutFees() {
    return (this.rm.originalPropertyVal - this.getDepositAmount()) - this.parseCB()
  }

  getMonthlyPayment() {
    return this.getLoanAmount() * (this.rm.interestRate/12) * (Math.pow((1 + (this.rm.interestRate/12)), this.rm.loanTerm * 12))/ ((Math.pow((1 + (this.rm.interestRate/12)), this.rm.loanTerm * 12)) - 1)
  }

  getMonthlyPaymentWithoutFees() {
    return this.getLoanAmountWithoutFees() * (this.rm.interestRate/12) * (Math.pow((1 + (this.rm.interestRate/12)), this.rm.loanTerm * 12))/ ((Math.pow((1 + (this.rm.interestRate/12)), this.rm.loanTerm * 12)) - 1)
  }
  
  getMonthlyFeeImpact() {
    return this.getMonthlyPayment() - this.getMonthlyPaymentWithoutFees()
  }

  getAPRC() {
    return this.rm.interestRate * 1.1
  }

  getOriginalLTV() {
    return this.getLoanAmount() / this.rm.originalPropertyVal
  }

  getDepositAmount() {
    return this.rm.originalPropertyVal * (this.rm.depositVal / 100)
  }

  showSnack(str: string) {
    this.snackBar.open(str, '', {
        duration: 2000,
    });
  }

  currencyInputChanged(value) {
    var num = value.replace(/[$,]/g, "");
    return Number(num);
  }
}
